import MuiIconButton from '@material-ui/core/IconButton';
import {withStyles} from '@material-ui/core/styles';
import React, {useMemo} from 'react';

import type {IconButtonProps} from '@material-ui/core/IconButton/IconButton';

type Variant = 'primary' | 'secondary' | 'tertiary';

export interface Props extends Omit<IconButtonProps, 'color'> {
    variant?: Variant | undefined;
}

export const IconButton = (props: Props) => {
    const {children, disabled = false, variant = 'primary', ...rest} = props;

    const IconButtonComponent = useMemo(() => {
        switch (variant) {
            case 'primary':
                return PrimaryIconButton;
            case 'secondary':
                return SecondaryIconButton;
            case 'tertiary':
                return TertiaryIconButton;
            default:
                return PrimaryIconButton;
        }
    }, [variant]);

    return (
        <IconButtonComponent disabled={disabled} disableRipple disableFocusRipple disableTouchRipple {...rest}>
            {children}
        </IconButtonComponent>
    );
};

const PrimaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary['500'],
        padding: '0.6rem',
        '&:hover': {
            backgroundColor: theme.palette.primary['700'],
        },
        '&:active': {
            backgroundColor: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const SecondaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.primary['500'],
        backgroundColor: theme.palette.grey['20'],
        borderColor: theme.palette.primary['500'],
        borderWidth: '0.2rem',
        borderStyle: 'solid',
        padding: '0.6rem',
        '&:hover': {
            color: theme.palette.primary['700'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.primary['700'],
        },
        '&:active': {
            color: theme.palette.primary['800'],
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            backgroundColor: theme.palette.grey['20'],
            borderColor: theme.palette.grey['100'],
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);

const TertiaryIconButton = withStyles((theme) => ({
    root: {
        color: theme.palette.grey['600'],
        padding: '0.6rem',
        '&:hover': {
            backgroundColor: theme.palette.primary['20'],
            color: theme.palette.primary['700'],
        },
        '&:active': {
            backgroundColor: theme.palette.primary['100'],
            color: theme.palette.primary['800'],
        },
        '&.Mui-focusVisible': {
            outlineColor: theme.palette.common.black,
            outlineStyle: 'solid',
            outlineWidth: '0.4rem',
            outlineOffset: '0.2rem',
            zIndex: 3,
        },
        '&:disabled': {
            color: theme.palette.grey['300'],
        },
    },
}))(MuiIconButton);
